import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch, useSelector } from "src/store";

import { createGroupMdmCommand } from "@dashboard/devices/api";
import apiClient from "@dashboard/files/api/apiClient";
import { useGetPolicyByIdQuery } from "@dashboard/policies/api";
import { usePolicyContentUpdate } from "@dashboard/policies/hooks/usePolicyContentUpdate/usePolicyContentUpdate";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { setPaneTab } from "@devices/store";
import { selectDevicesByGroupId } from "@devices/store/selectors/devices";
import { Group } from "@devices/types";
import { FileInfo } from "@files/types";
import { DialogProps, useTheme } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { PrimaryButton } from "@shared/CustomButton";
import { RadioField, RadioFieldGorup } from "@shared/form/RadioField";
import { Modal } from "@shared/Modal/Modal";
import { showErrorToast } from "@shared/toasts/Toasts";

type FormValues = {
    action: "installImmediately" | "addToPolicy";
};
type Props = Omit<DialogProps, "onClose"> & {
    group: Group | undefined;
    file: FileInfo;
    onClose: () => void;
};
export const PickInstallationTypeDialog = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const theme = useTheme();
    const dispatch = useDispatch();
    const devices = useSelector((state) => selectDevicesByGroupId(state, props.group?.id));
    const { data: workspace = null } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();

    const cannotUpdatePolicy = workspaceAbility(workspace).cannot("update", "Policy");
    const isSubscriptionExpired = useCurrentSubscriptionExpired();

    const { data: assignedPolicyResponse, isLoading: isLoadingPolicies } = useGetPolicyByIdQuery(
        props.group?.policyId && !isSubscriptionExpired ? { id: props.group.policyId } : skipToken,
    );
    const assignedPolicy = assignedPolicyResponse?.model;
    const { updateContent } = usePolicyContentUpdate();

    const otherGroupsCount = assignedPolicy?.groupIds?.filter((id) => id !== props.group?.id).length ?? 0;

    const installImmediately = async () => {
        if (!props.group) {
            return;
        }
        try {
            const url = await apiClient.getPresignedDownloadUrl(props.file.id);
            await createGroupMdmCommand({
                groupId: props.group.id,
                json: {
                    command: "installPackage",
                    source: "url",
                    url: url,
                    package: props.file.metadata?.file?.apk?.manifest?.package,
                    version: props.file.metadata?.file?.apk?.manifest?.versioncode,
                },
            });
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        }
    };

    const addToPolicy = async () => {
        if (!assignedPolicy) {
            return;
        }
        const manifest = props.file.metadata?.file?.apk?.manifest;
        const command: API.Policy = {
            command: "installPackage",
            source: "storage",
            file: props.file.id,
            ...(manifest?.package ? { package: manifest.package } : {}),
            ...(manifest?.versioncode ? { version: manifest.versioncode } : {}),
        };

        const fallbackContent = assignedPolicy?.content ?? [];
        await updateContent({
            id: assignedPolicy.id,
            name: assignedPolicy.name,
            content: [...fallbackContent, command],
        });
    };

    const onSubmit = async (values: FormValues) => {
        switch (values.action) {
            case "installImmediately":
                await installImmediately();
                dispatch(setPaneTab("COMMANDS"));
                break;
            case "addToPolicy":
                await addToPolicy();
                dispatch(setPaneTab("POLICY"));
                break;
            default:
                break;
        }
    };

    const getAddToPolicyHelperText = () => {
        if (isLoadingPolicies) {
            return t("modals:installationType.input.addToPolicy.helperText");
        }
        if (!props.group?.policyId) {
            return t("modals:installationType.input.addToPolicy.helperTextNoPolicy");
        }
        if (otherGroupsCount > 0) {
            return t("modals:installationType.input.addToPolicy.helperTextShared", { count: otherGroupsCount });
        }
        return t("modals:installationType.input.addToPolicy.helperText");
    };

    const getAddToPolicyTooltip = () => {
        if (cannotUpdatePolicy) {
            return t("common:tooltip.cannotManagePolicies");
        }
        if (!devices.length) {
            return t("modals:installationType.tooltip.noDevices");
        }
        if (!isLoadingPolicies && !props.group?.policyId) {
            return t("modals:installationType.tooltip.noPolicyAssigned");
        }
        return "";
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            initialValues={{ action: "installImmediately" }}
            render={({ handleSubmit, submitting, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:installationType.title")}
                        endButton={
                            <PrimaryButton onClick={form.submit} disabled={submitting} loading={submitting}>
                                {t("common:button.install")}
                            </PrimaryButton>
                        }
                        {...props}
                    >
                        <RadioFieldGorup name="action">
                            <RadioField
                                label={t("modals:installationType.input.installImmediately.caption")}
                                value="installImmediately"
                                helperText={t("modals:installationType.input.installImmediately.helperText")}
                                style={{ marginBottom: theme.spacing(5) }}
                            />

                            <RadioField
                                label={t("modals:installationType.input.addToPolicy.caption")}
                                value="addToPolicy"
                                helperText={getAddToPolicyHelperText()}
                                style={{ marginBottom: theme.spacing(4) }}
                                disabled={!devices.length || !assignedPolicy || cannotUpdatePolicy}
                                tooltip={getAddToPolicyTooltip()}
                            />
                        </RadioFieldGorup>
                    </Modal>
                </form>
            )}
        />
    );
};
